/**
 * Auth service ΓÇö API communication only (M1).
 * Access JWT stays in memory; refresh cookie is primary, local backup for reliability.
 */
import apiClient, { setAccessToken, clearAccessToken, setTokenRefreshHandler } from './apiClient';
import {
  touchSession,
  isBrowserSessionAlive,
  endBrowserSession,
} from './sessionLifetime';

const REFRESH_STORAGE_KEY = '3dkey_refresh_backup';
export const LOGIN_COMPLETE_KEY = '3dkey_login_complete';

function saveRefreshBackup(token) {
  try {
    if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
    else localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch (e) {
    // ignore storage errors
  }
}

/**
 * The stored refresh token is only honoured within the browser session that
 * created it. Left ungated it would sign the user straight back in after they
 * had closed the app, which is precisely what the session is meant to end.
 */
function readRefreshBackup() {
  if (!isBrowserSessionAlive()) return null;
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
}

function clearRefreshBackup() {
  saveRefreshBackup(null);
}

/**
 * Records that this browser has completed sign-in.
 *
 * localStorage, not sessionStorage: route guards refuse access without this
 * flag, and a per-tab flag meant a valid server session was rejected in any
 * new tab — the user was bounced to the login form while genuinely signed in.
 */
export function markLoginComplete() {
  try {
    localStorage.setItem(LOGIN_COMPLETE_KEY, '1');
  } catch (e) {
    // ignore
  }
  // Signing in starts the browser session this flag is scoped to.
  touchSession();
}

export function isLoginComplete() {
  try {
    // sessionStorage is still read so an existing signed-in tab is not
    // logged out by this change.
    if (sessionStorage.getItem(LOGIN_COMPLETE_KEY) === '1') return true;
    // The shared flag is what lets a second tab see an existing session, so it
    // stays in localStorage — but only counts while the app is actually
    // running somewhere, or it would survive closing the app.
    return (
      localStorage.getItem(LOGIN_COMPLETE_KEY) === '1' && isBrowserSessionAlive()
    );
  } catch (e) {
    return false;
  }
}

export function clearLoginComplete() {
  try {
    localStorage.removeItem(LOGIN_COMPLETE_KEY);
    sessionStorage.removeItem(LOGIN_COMPLETE_KEY);
  } catch (e) {
    // ignore
  }
}

/** Clear in-memory JWT + refresh backup + login flag (local session only). */
export function clearLocalSession() {
  clearAccessToken();
  clearRefreshBackup();
  clearLoginComplete();
  endBrowserSession();
}

/**
 * Drops any credentials left over from a previous run of the app.
 *
 * Called once at launch, before anything tries to refresh. Without it the
 * tokens would merely be ignored while still sitting in storage, and any later
 * sign-in during the same browser session would revive them.
 */
export function purgeEndedBrowserSession() {
  if (isBrowserSessionAlive()) return false;
  const hadSession = Boolean(readRawRefreshBackup() || readRawLoginComplete());
  clearAccessToken();
  clearRefreshBackup();
  clearLoginComplete();
  return hadSession;
}

function readRawRefreshBackup() {
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
}

function readRawLoginComplete() {
  try {
    return localStorage.getItem(LOGIN_COMPLETE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export async function register({ email, password, name, acceptTerms }) {
  const res = await apiClient.post('/auth/register', { email, password, name, acceptTerms });
  const data = res.data.data;
  if (data.accessToken) {
    setAccessToken(data.accessToken);
    saveRefreshBackup(data.refreshToken);
    markLoginComplete();
  }
  return data;
}

export async function login({ email, password }) {
  const res = await apiClient.post('/auth/login', { email, password });
  const data = res.data.data;
  setAccessToken(data.accessToken);
  saveRefreshBackup(data.refreshToken);
  markLoginComplete();
  return data;
}

export async function verifyEmail({ token }) {
  const res = await apiClient.post('/auth/verify-email', { token });
  const data = res.data.data;
  setAccessToken(data.accessToken);
  saveRefreshBackup(data.refreshToken);
  markLoginComplete();
  return data;
}

export async function resendVerification({ email }) {
  const res = await apiClient.post('/auth/resend-verification', { email });
  return res.data.data;
}

export async function forgotPassword({ email }) {
  const res = await apiClient.post('/auth/forgot-password', { email });
  return res.data.data;
}

export async function resetPassword({ token, password }) {
  const res = await apiClient.post('/auth/reset-password', { token, password });
  clearAccessToken();
  clearRefreshBackup();
  clearLoginComplete();
  return res.data.data;
}

export async function refreshSession() {
  const backup = readRefreshBackup();
  const res = await apiClient.post(
    '/auth/refresh',
    backup ? { refreshToken: backup } : {},
    { skipAuthRefresh: true }
  );
  const data = res.data.data;
  setAccessToken(data.accessToken);
  if (data.refreshToken) saveRefreshBackup(data.refreshToken);
  return data;
}

export async function logout() {
  try {
    const backup = readRefreshBackup();
    await apiClient.post('/auth/logout', backup ? { refreshToken: backup } : {});
  } finally {
    clearAccessToken();
    clearRefreshBackup();
    clearLoginComplete();
  }
  return { loggedOut: true };
}

export async function loginWithGoogle({ idToken, organization, country, acceptTerms }) {
  const res = await apiClient.post('/auth/google', { idToken, organization, country, acceptTerms });
  const data = res.data.data;
  if (data.accessToken) {
    setAccessToken(data.accessToken);
    saveRefreshBackup(data.refreshToken);
    markLoginComplete();
  }
  return data;
}

export async function updateProfile({ organization, country, name }) {
  const res = await apiClient.patch('/auth/profile', { organization, country, name });
  return res.data.data;
}

export async function proceedAccessGate({ email, name, organization, country, password }) {
  const res = await apiClient.post('/auth/gate-proceed', {
    email,
    name,
    organization,
    country,
    password,
  });
  const data = res.data.data;
  if (!data.isNewUser && !data.requiresEmailVerification && data.accessToken) {
    setAccessToken(data.accessToken);
    saveRefreshBackup(data.refreshToken);
    markLoginComplete();
  }
  return data;
}

export async function enterAccessGate({ email, name, organization, country }) {
  const res = await apiClient.post('/auth/gate-enter', {
    email,
    name,
    organization,
    country,
  });
  const data = res.data.data;
  setAccessToken(data.accessToken);
  saveRefreshBackup(data.refreshToken);
  markLoginComplete();
  return data;
}

export async function getMe() {
  const res = await apiClient.get('/auth/me');
  return res.data.data;
}

const authService = {
  register,
  login,
  verifyEmail,
  resendVerification,
  forgotPassword,
  resetPassword,
  refreshSession,
  logout,
  getMe,
  enterAccessGate,
  proceedAccessGate,
  loginWithGoogle,
  updateProfile,
  markLoginComplete,
  isLoginComplete,
  clearLoginComplete,
  clearLocalSession,
  purgeEndedBrowserSession,
};

setTokenRefreshHandler(refreshSession);

export default authService;
